import type { NodeContent } from '~/interfaces/page';

import { findTipTapContentNode } from './findTipTapContentNode';

interface ArchivePaginationParams {
  isArchive: boolean;
  isArchiveRoute: boolean;
  paginateWithNumbers: boolean;
  search: string;
}

export interface ArchivePagination {
  urlPage: number;
  useUrlPagination: boolean;
  isCrawlableLoadMore: boolean;
  linksToCollectionHub: boolean;
  shouldOffsetArchivePosts: boolean;
}

export interface ArchivePaginationData {
  totalPages: number;
  postCount: number;
}

export const PAGE_PARAM = 'page';

export const ARCHIVE_PAGINATION_PARAMS = [PAGE_PARAM, 'offer_id'];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const parseUrlPage = (search: string): number => {
  const page = Number.parseInt(new URLSearchParams(search).get(PAGE_PARAM) || '1', 10);
  return Number.isNaN(page) || page < 1 ? 1 : page;
};

export const buildArchivePaginationSearch = (search: string, page: number): string => {
  const current = new URLSearchParams(search);
  const params = new URLSearchParams();

  ARCHIVE_PAGINATION_PARAMS.forEach((key) => {
    const value = current.get(key);
    if (value) params.set(key, value);
  });

  params.set(PAGE_PARAM, String(page));

  return params.toString();
};

export const findArchivePaginationData = (content?: NodeContent): ArchivePaginationData | null => {
  if (!content) return null;

  const archiveNode = findTipTapContentNode(content, 'post', (node) => {
    const nodeAttrs: unknown = 'attrs' in node ? node.attrs : undefined;
    return isRecord(nodeAttrs) && nodeAttrs.postGroupType === 'archive';
  });

  const attrs: unknown = archiveNode && 'attrs' in archiveNode ? archiveNode.attrs : undefined;
  const data: unknown = isRecord(attrs) ? attrs.data : undefined;
  const pagination: unknown = isRecord(data) ? data.pagination : undefined;

  if (!isRecord(pagination) || typeof pagination.total_pages !== 'number') return null;

  const posts: unknown = isRecord(data) ? data.posts : undefined;

  return {
    totalPages: pagination.total_pages,
    postCount: Array.isArray(posts) ? posts.length : 0,
  };
};

export const resolveArchivePagination = ({
  isArchive,
  isArchiveRoute,
  paginateWithNumbers,
  search,
}: ArchivePaginationParams): ArchivePagination => {
  const urlPage = parseUrlPage(search);
  const isArchiveOnRoute = isArchive && isArchiveRoute;

  const useUrlPagination = isArchiveOnRoute && paginateWithNumbers;
  const isCrawlableLoadMore = isArchiveOnRoute && !paginateWithNumbers;
  const linksToCollectionHub = isArchive && !isArchiveRoute;
  const isDeepCrawlablePage = isCrawlableLoadMore && urlPage > 1;
  const shouldOffsetArchivePosts = isArchive && !useUrlPagination && !isDeepCrawlablePage;

  return { urlPage, useUrlPagination, isCrawlableLoadMore, linksToCollectionHub, shouldOffsetArchivePosts };
};
